import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { Skeleton, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import type { ContentLanguage } from '../../model/types';

export interface LocaleSwitcherProps {
  languages: ContentLanguage[];
  value: string;
  onChange: (locale: string) => void;
  isLoading: boolean;
  isError: boolean;
  /** `catalog.structure.view` — gates the "Add languages" link (languages precedent). */
  canViewLanguageSettings: boolean;
}

/**
 * The tenant's active content languages, default first — **never a hard-wired locale** (the
 * defect the page this replaces shipped: `DEFAULT_BOT_MESSAGE_LOCALE = 'ru'`). One language only
 * still renders (as a single disabled toggle) so the merchant learns where languages come from;
 * an empty/unavailable registry falls back to a caption rather than blocking the page.
 */
export function LocaleSwitcher({
  languages,
  value,
  onChange,
  isLoading,
  isError,
  canViewLanguageSettings,
}: LocaleSwitcherProps) {
  const { t } = useTranslation('chatbot');

  if (isLoading) {
    return <Skeleton variant="rounded" width={220} height={34} />;
  }

  const active = languages.filter((l) => l.active);
  const sorted = [...active].sort((a, b) =>
    a.defaultLocale === b.defaultLocale ? 0 : a.defaultLocale ? -1 : 1,
  );

  if (isError || sorted.length === 0) {
    return (
      <Stack spacing={0.5}>
        <ToggleButtonGroup
          exclusive
          value={value}
          size="small"
          aria-label={t('templates.localeSwitcherAriaLabel')}
        >
          <ToggleButton value={value} disabled>
            {value.toUpperCase()}
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          {isError ? (
            t('templates.localeSwitcherLoadError')
          ) : canViewLanguageSettings ? (
            <RouterLink to="/settings/languages">{t('templates.localeSwitcherEmpty')}</RouterLink>
          ) : (
            t('templates.localeSwitcherEmpty')
          )}
        </Typography>
      </Stack>
    );
  }

  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      size="small"
      onChange={(_event, next) => {
        if (next) onChange(next);
      }}
      aria-label={t('templates.localeSwitcherAriaLabel')}
    >
      {sorted.map((language) => {
        const nameAndCode = `${language.name} (${language.locale})`;
        return (
          <ToggleButton
            key={language.locale}
            value={language.locale}
            disabled={sorted.length === 1}
            aria-label={
              language.defaultLocale
                ? t('templates.localeDefaultAriaLabel', { name: nameAndCode })
                : nameAndCode
            }
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              {language.defaultLocale ? <StarRoundedIcon sx={{ fontSize: 14 }} /> : null}
              <span>
                {language.name} ({language.locale})
              </span>
            </Stack>
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
}
